
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from "vue";
import { useMessengerHeader } from "@/composables/messengerHeader";
import { WidgetConfigurationChannelInfo } from "@/types/inboundWebhookPayload";
import { TransferChannelOption } from "@/components/modals/TransferConversationModal.vue";
import { getHumanChannel } from "@/util/formatting";
import { showTransferConversationDialog } from "@/dialogs/TransferConversationDialog";
import {
  FormKitGroupValue,
  FormKitNode,
  FormKitSchemaNode,
} from "@formkit/core";
import {
  CountryCode,
  getCountryCallingCode,
  isSupportedCountry,
  AsYouType,
} from "libphonenumber-js";
import { useApp } from "@/composables/use-app";
import { useChatConnection } from "@/composables/chatConnection";
import { useConversation } from "@/composables/conversation";
import { closeDialog } from "vue3-promise-dialog";
import Button from "@/components/Button.vue";
import { useI18n } from "vue-i18n";

const channelTypeToFormkitTypeMap: Record<
  WidgetConfigurationChannelInfo["channel"],
  string
> = {
  email: "email",
  sms: "text",
  whatsapp: "text",
};

const fieldTypeValidationMap: Record<
  WidgetConfigurationChannelInfo["channel"],
  string
> = {
  email: "required|email",
  sms: "required|phone",
  whatsapp: "required|phone",
};

const phoneNumberChannels = ["sms", "whatsapp"];

export default defineComponent({
  name: "TransferConversationDestinationModal",
  components: {
    Button,
  },
  props: {
    data: {
      type: Object as PropType<TransferChannelOption>,
      required: true,
    },
    backAction: {
      type: String,
      default: "transfer-conversation-modal",
    },
  },
  setup(props) {
    const { height: headerHeight, setBackAction } = useMessengerHeader();
    const { requestTransferConversation } = useChatConnection();
    const { visitorCountryCode } = useApp();
    const { conversation } = useConversation();
    const form = ref();
    const { t } = useI18n();

    const channelTypeToFormkitFieldLabelMap: Record<
      WidgetConfigurationChannelInfo["channel"],
      string
    > = {
      email: t("transferConversationDestinationModal.emailAddress"),
      sms: t("transferConversationDestinationModal.phoneNumber"),
      whatsapp: t("transferConversationDestinationModal.phoneNumber"),
    };

    const channelTypeToFormkitFieldHintMap: Record<
      WidgetConfigurationChannelInfo["channel"],
      string | undefined
    > = {
      email: undefined,
      sms: t("transferConversationDestinationModal.countryCodeNotice"),
      whatsapp: t("transferConversationDestinationModal.countryCodeNotice"),
    };

    const returnValue = () => true;

    const containerStyle = computed(
      () =>
        `top: ${headerHeight.value}px; height: calc(100% - ${headerHeight.value}px)`
    );

    const channelDescription = computed(() =>
      getHumanChannel(props.data.channel, false)
    );

    const schema = computed(() => {
      const res: Array<FormKitSchemaNode> = [];

      const obj: FormKitSchemaNode = {
        name: "address",
        $formkit: channelTypeToFormkitTypeMap[props.data.channel],
        validation: fieldTypeValidationMap[props.data.channel],
        label: channelTypeToFormkitFieldLabelMap[props.data.channel],
        help: channelTypeToFormkitFieldHintMap[props.data.channel],
        autofocus: true,
      };

      if (props.data.suggestionAddress) {
        obj.value = props.data.suggestionAddress;
      } else if (
        phoneNumberChannels.includes(props.data.channel) &&
        visitorCountryCode.value &&
        isSupportedCountry(visitorCountryCode.value)
      ) {
        const countryCode = getCountryCallingCode(
          visitorCountryCode.value as CountryCode
        );

        if (countryCode) {
          obj.value = "+" + countryCode;
        }
      }

      res.push(obj);

      return res;
    });

    const submit = async (form: FormKitGroupValue) => {
      if (
        !("address" in form) ||
        !form.address ||
        !(form.address as string)?.trim()
      ) {
        console.warn("Missing address, could not request transfer");
        return;
      }

      requestTransferConversation(props.data.channel, form.address as string);
      return new Promise((resolve) => setTimeout(resolve, 10000));
    };

    watch(
      () => conversation.value?.isTransferred,
      () => {
        if (conversation.value?.isTransferred) {
          closeDialog();
        }
      }
    );

    onMounted(() => {
      setBackAction(() => {
        if (props.backAction === "transfer-conversation-modal") {
          showTransferConversationDialog();
        } else {
          closeDialog();
        }
      });

      // Focus first input
      setTimeout(() => {
        form.value?.querySelector("input")?.focus();
      }, 100);
    });

    onUnmounted(() => {
      setBackAction(null);
    });

    return {
      returnValue,
      headerHeight,
      containerStyle,
      channelDescription,
      schema,
      submit,
      form,
      t,
    };
  },
});
